import { oauth, memoizeOAuthGet } from './utils';

// currently, the only PoD division is Fulfill Engine
export const POD_DIVISION_ID = '1dbd17f3-5c48-4619-9d6c-713a2c0e80bf';
export const POD_DIVISION_NAME = 'Fulfill Engine';
export const POD_PRODUCT_SOURCE = 'pod-products';
export const POD_BASE_SIZE_GROUP_LABEL = 'XS-XL';
export const POD_ONE_SIZE_LABEL = 'One Size';
export const POD_BASE_SIZE_NAMES = ['XS', 'S', 'M', 'L', 'XL'];
export const POD_SURCHARGE_SIZE_NAMES = ['2XL', '3XL', '4XL', '5XL', '6XL'];

export const normalizePodSizeName = (sizeName: unknown): string =>
  String(sizeName ?? '').trim().toUpperCase();

export const isPoDSource = (source: unknown): boolean => POD_PRODUCT_SOURCE === source;

export const normalizePodSizeLabel = (
  sizeName: string | null | undefined,
): string => {
  const normalized = normalizePodSizeName(sizeName);
  if (POD_BASE_SIZE_NAMES.includes(normalized)) {
    return POD_BASE_SIZE_GROUP_LABEL;
  }
  if (POD_SURCHARGE_SIZE_NAMES.includes(normalized)) {
    return normalized;
  }
  return POD_ONE_SIZE_LABEL;
};

export interface PodProduct {
  pod_supplier_id: string;
  product_name: string;
  product_image: string | null;
  ext_product_id: string;
  min_price: number;
  max_price: number;
  product_supplier_code: string;
  currency: string;
  source: string;
  [key: string]: unknown;
}

export interface PodSkuDetail {
  pod_sku: string;
  color: string | null;
  size: string | null;
  weight: number | null;
  price: number | null;
}

export interface PodImageDetail {
  image_type: string;
  url: string | null;
  color_name: string | null;
  hex_code: string | null;
  zoom_url: string | null;
  thumbnail_url: string | null;
}

export interface PodProductDetail {
  pod_product_id: string;
  name: string;
  description: string | null;
  skus: PodSkuDetail[];
  images: PodImageDetail[];
}

export interface PodChargePrice {
  min_qty: number;
  dim_min: number;
  price: number;
}

export interface PodCharge {
  pod_charge_id: string;
  name: string;
  charge_type: "RUN" | "SETUP";
  uom: string | null;
  scope?: "item" | "location";
  prices: PodChargePrice[];
}

export interface PodDecorationOptions {
  charges?: PodCharge[];
  embroidery_limits?: {
    included_stitches?: number;
    max_stitches?: number;
    max_logo_height_in?: number;
    max_logo_width_in?: number;
  };
  thread_colors?: Array<{
    brand?: string;
    code?: string;
    description?: string;
    hex?: string;
  }>;
  font_families?: Array<{
    family?: string;
    name?: string;
  }>;
}

export interface PodDecoration {
  pod_decoration_id: string;
  name: string;
  options?: PodDecorationOptions;
}

export interface PodLocation {
  pod_location_id: string;
  name: string;
  image_type?: string | null;
  decorations: PodDecoration[];
}

export interface PodProductConfiguration {
  locations: PodLocation[];
}

export async function fetchPodProducts(params: {
  keyword?: string;
  limit: number;
  offset: number;
  minPrice?: string;
  maxPrice?: string;
}): Promise<PodProduct[]> {
  const query: Record<string, string | number> = {
    division_id: POD_DIVISION_ID,
    limit: params.limit,
    offset: params.offset,
  };
  if (params.keyword) {
    query.keyword = params.keyword;
  }
  if (params.minPrice) {
    query.min_price = params.minPrice;
  }
  if (params.maxPrice) {
    query.max_price = params.maxPrice;
  }

  const { json } = await (oauth('GET', 'pod/products', query, undefined, undefined, undefined, false) as Promise<{ json: any; response: Response }>);

  return json?.data ?? [];
}

export async function fetchPodProduct(extProductId: string): Promise<PodProductDetail> {
  const { json } = await (memoizeOAuthGet(
    `pod/products/${encodeURIComponent(extProductId)}`,
    { division_id: POD_DIVISION_ID },
  ) as Promise<{ json: any; response: Response }>);
  return json;
}

export async function getPodProductConfiguration(params: {
  extProductId?: string | null;
  divisionId?: string;
}): Promise<PodProductConfiguration> {
  if (!params.extProductId) {
    return { locations: [] };
  }

  const { json } = await (memoizeOAuthGet(
    `pod/products/${encodeURIComponent(params.extProductId)}/configuration`,
    { division_id: params.divisionId ?? POD_DIVISION_ID },
  ) as Promise<{ json: PodProductConfiguration; response: Response }>);

  return json ?? { locations: [] };
}
